import React, { useMemo } from 'react'
import { cotationReferenceLabel } from '../utils/cotationReference'
import { MarketPositionScale } from './MarketPositionScale'
import { MarchePanelInsights } from './MarchePanelInsights'
import { getAnnonceCompletenessIssues, describeIncompleteAnnonce } from '../utils/incompleteListing'
import { useBackToClose } from '../hooks/useBackToClose'

export { MarketPositionScale } from './MarketPositionScale'

const VW_BLUE = '#001E50'
const SUR = '#A32D2D'
const DORT = '#A07530'
const SOUS = '#1F4D82'
const RADIUS_BLOCK = 6
const RADIUS_PILL = 4

export function formatChf(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—'
  return `${Number(n).toLocaleString('fr-CH')} CHF`
}

/** Titre affiché type « Honda CB 650 R » : mots courts en majuscules, reste capitalisé. */
export function formatVehicleDisplayTitle(titre) {
  const raw = String(titre || '').trim()
  if (!raw) return '—'
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      if (/^\d+[a-z]?$/i.test(word)) return word.toUpperCase()
      if (word.length <= 3 && /^[a-z]+$/i.test(word)) return word.toUpperCase()
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

function concurrenceBadgeCompact(row) {
  const c = row?.concurrence
  if (!c) return null
  const n = Number(c.nb_concurrents) || 0
  const ecartChf = Number(c.ecart_vs_median_chf)
  const ecartTxt = Number.isFinite(ecartChf) ? `${ecartChf > 0 ? '+' : ''}${ecartChf.toLocaleString('fr-CH')} CHF` : ''
  const s = c.situation
  if (s === 'critique' || s === 'expose') {
    return {
      key: s,
      text: ecartTxt ? `${n} concurrents · ${ecartTxt} vs médiane` : `${n} concurrents`,
      bg: s === 'critique' ? 'rgba(127,29,29,0.35)' : 'rgba(163,45,45,0.12)',
      color: s === 'critique' ? '#fecaca' : '#e7c08f',
      border: s === 'critique' ? '1px solid rgba(248,113,113,0.45)' : '1px solid rgba(224,184,120,0.35)',
    }
  }
  if (s === 'exclusivite') {
    return { key: s, text: 'Exclusivité locale', bg: 'rgba(0,30,80,0.22)', color: '#93c5fd', border: '1px solid rgba(0,30,80,0.45)' }
  }
  if (s === 'sature') {
    return {
      key: s,
      text: `${n} concurrents sur ce modèle`,
      bg: 'rgba(255,255,255,0.06)',
      color: 'rgba(226,232,240,0.85)',
      border: '1px solid rgba(148,163,184,0.22)',
    }
  }
  if (s === 'avantage') {
    return {
      key: s,
      text: 'Moins cher que la concurrence',
      bg: 'rgba(22,101,52,0.35)',
      color: '#86efac',
      border: '1px solid rgba(74,222,128,0.45)',
    }
  }
  return null
}

function situationConcurrenceCopy(situation) {
  const map = {
    exclusivite: 'Peu d’annonces équivalentes localement — visibilité favorable.',
    critique: 'Nombreuses annonces concurrentes sur ce créneau prix.',
    expose: 'Prix au-dessus de la médiane concurrente — exposition au filtre prix.',
    sature: 'Offre dense sur ce modèle — différenciation utile.',
    dans_le_marche: 'Concurrence équilibrée sur ce modèle.',
    avantage: 'Prix inférieur à la médiane des annonces comparables.',
  }
  return map[situation] || map.dans_le_marche
}

function situationBadgeLarge(situation) {
  const map = {
    critique: { label: 'Critique', bg: 'rgba(127,29,29,0.35)', color: '#fecaca', border: '1px solid rgba(248,113,113,0.45)' },
    expose: { label: 'Exposé', bg: 'rgba(163,45,45,0.12)', color: '#e7c08f', border: '1px solid rgba(224,184,120,0.35)' },
    sature: { label: 'Saturé', bg: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.85)', border: '1px solid rgba(148,163,184,0.22)' },
    dans_le_marche: { label: 'Marché', bg: 'rgba(255,255,255,0.06)', color: 'rgba(226,232,240,0.75)', border: '1px solid rgba(255,255,255,0.12)' },
    avantage: { label: 'Avantage', bg: 'rgba(22,101,52,0.4)', color: '#bbf7d0', border: '1px solid rgba(74,222,128,0.45)' },
    exclusivite: { label: 'Exclusivité', bg: 'rgba(0,30,80,0.28)', color: '#bfdbfe', border: '1px solid rgba(0,30,80,0.5)' },
  }
  return map[situation] || map.dans_le_marche
}

function computeStockPanelDepreciation(annonce, depreciations) {
  const a = annonce
  const joursEnLigneNum =
    a.jours_en_ligne != null && Number.isFinite(Number(a.jours_en_ligne)) ? Number(a.jours_en_ligne) : null
  if (joursEnLigneNum == null || joursEnLigneNum <= 0 || !a.valeur_marche) return null
  const fallbackDep = (a.valeur_marche * 0.004) / 30
  const depMeta = depreciations[a.model]
  const depModele = Number(depMeta?.perDay)
  const depNbVentes = Number(depMeta?.nbVentes) || 0
  const depMaxPct = (a.valeur_marche * 0.018) / 30
  const depHardCap = 8
  let depBrut = Number.isFinite(depModele) && depModele > 0 ? depModele : fallbackDep
  if (a.confiance === 'faible') depBrut *= 0.8
  else if (a.confiance === 'moyenne') depBrut *= 0.9
  if (depNbVentes > 0 && depNbVentes < 10) depBrut *= 0.85
  if (joursEnLigneNum < 15) depBrut *= 0.7
  const depParJour = Math.min(depBrut, depMaxPct, depHardCap)
  const depreciationParJour = Math.max(0, Math.round(depParJour))
  const perteTotale = depreciationParJour * joursEnLigneNum
  return { depreciationParJour, perteTotale, isUrgent: depreciationParJour > 5 }
}

function shouldHideGenericSynth(text) {
  if (!text || typeof text !== 'string') return false
  const t = text.trim()
  if (t.length > 220) return false
  return /bien positionn(ée|e)|au prix du marché|prix du marché|correctement positionn/i.test(t)
}

export function marketScaleFromAnnonce(a) {
  let lo = Number(a?.fourchette_basse)
  let hi = Number(a?.fourchette_haute)
  const med = Number(a?.valeur_marche)
  const disp = Number(a?.dispersion_pct)
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    if (Number.isFinite(med) && Number.isFinite(disp) && disp > 0) {
      lo = Math.round(med * (1 - disp / 100))
      hi = Math.round(med * (1 + disp / 100))
    } else if (Number.isFinite(med)) {
      lo = Math.round(med * 0.88)
      hi = Math.round(med * 1.12)
    }
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
    if (Number.isFinite(med)) {
      const pad = Math.max(500, Math.round(med * 0.1))
      lo = med - pad
      hi = med + pad
    } else return null
  }
  let median = Number.isFinite(med) ? med : (lo + hi) / 2
  median = Math.min(hi, Math.max(lo, median))
  return { lo, hi, median }
}

function similarRowEcartStyle(pctVsStock) {
  if (!Number.isFinite(pctVsStock)) return { color: 'var(--text-tertiary)' }
  if (pctVsStock > 8) return { color: '#e7a39a' }
  if (pctVsStock > 3) return { color: '#e7c08f' }
  if (pctVsStock < -8) return { color: '#9ec5f7' }
  if (pctVsStock < -3) return { color: '#93c5fd' }
  return { color: 'var(--text-secondary)' }
}

function getStockPhotoUrls(a) {
  const list = [
    a?.image_url,
    a?.image_principale_url,
    ...(Array.isArray(a?.images) ? a.images.map((x) => x?.url || x) : []),
  ]
    .map((x) => String(x || '').trim())
    .filter(Boolean)
  return [...new Set(list)].slice(0, 8)
}

/** Alerte unique : bordure gauche + fond léger, sans emoji. */
function getPriorityAlert(a, reco) {
  if (a.skip_cotation) return null
  const ec = Number(a.ecart_pct)
  const j = Number(a.jours_en_ligne)
  const pos = String(a.positionnement || '').toLowerCase()
  const sur = pos.includes('surcôt') || pos.includes('surcot') || (Number.isFinite(ec) && ec >= 10)
  const sous = pos.includes('sous-cot') || pos.includes('sous cot') || (Number.isFinite(ec) && ec <= -10)
  const dort = Number.isFinite(j) && j > 90

  const bodyFromReco = reco && typeof reco.detail === 'string' ? reco.detail : null

  if (sur) {
    return {
      key: 'sur',
      title: 'Réviser le prix',
      body:
        bodyFromReco ||
        (Number.isFinite(ec)
          ? `Écart +${ec}% vs référence marché — ajuster le prix pour retrouver du volume.`
          : 'Prix au-dessus du marché — ajuster pour retrouver du volume.'),
      border: `2px solid ${SUR}`,
      bg: 'rgba(248,113,113,0.06)',
    }
  }
  if (dort) {
    return {
      key: 'dort',
      title: 'Accélérer la vente',
      body:
        bodyFromReco ||
        (Number.isFinite(j) ? `${j} j en ligne — visibilité, garantie ou ajustement de prix à étudier.` : 'Rotation stock à accélérer.'),
      border: `2px solid ${DORT}`,
      bg: 'rgba(224,184,120,0.08)',
    }
  }
  if (sous) {
    return {
      key: 'sous',
      title: 'Remonter le prix',
      body:
        bodyFromReco ||
        (Number.isFinite(ec)
          ? `Écart ${ec}% vs référence — marge disponible si la demande le permet.`
          : 'Prix en dessous du marché — marge disponible si la demande le permet.'),
      border: `2px solid ${SOUS}`,
      bg: 'rgba(96,165,250,0.08)',
    }
  }
  if (reco && reco.urgence === 'orange') {
    return {
      key: 'reco-orange',
      title: reco.action || 'Action recommandée',
      body: bodyFromReco || 'Surveiller visibilité et concurrence sur ce créneau.',
      border: `2px solid ${DORT}`,
      bg: 'rgba(224,184,120,0.08)',
    }
  }
  if (reco && reco.urgence === 'rouge') {
    return {
      key: 'reco-red',
      title: reco.action || 'Réviser le prix',
      body: bodyFromReco || '',
      border: `2px solid ${SUR}`,
      bg: 'rgba(248,113,113,0.06)',
    }
  }
  return null
}

export function SectionRule() {
  return <div style={{ height: 1, background: 'rgba(148,163,184,0.14)', margin: '18px 0' }} />
}

export function MetricCell({ label, value, sub, emphasizeValue = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: emphasizeValue ? 16 : 14,
          fontWeight: emphasizeValue ? 650 : 500,
          color: 'var(--text)',
          lineHeight: 1.3,
          fontVariantNumeric: emphasizeValue ? 'tabular-nums' : undefined,
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div> : null}
    </div>
  )
}

/**
 * Panneau latéral détail véhicule (Mon Stock) — hiérarchie lisible, données inchangées.
 */
export function StockVehiclePanel({
  annonce: a,
  depreciations,
  reco,
  concurrenceRow,
  onClose,
  onNavigateConcurrence,
  onCoterMoto,
  coterStock,
  token,
}) {
  // Bouton retour système (mobile) → ferme le panneau au lieu de quitter l'app.
  useBackToClose(Boolean(a), onClose)
  const depData = useMemo(() => computeStockPanelDepreciation(a, depreciations || {}), [a, depreciations])
  const joursEnLigneNum =
    a.jours_en_ligne != null && Number.isFinite(Number(a.jours_en_ligne)) ? Number(a.jours_en_ligne) : null

  const modelTrim = a.model ? String(a.model).trim() : ''
  const versionTrim = a.version ? String(a.version).trim() : ''
  const showVersionSubtitle = Boolean(versionTrim && versionTrim.toLowerCase() !== modelTrim.toLowerCase())

  const subtitleParts = [
    a.annee,
    a.km != null ? `${Number(a.km).toLocaleString('fr-CH')} km` : null,
    modelTrim || null,
    showVersionSubtitle ? `Version ${versionTrim}` : null,
    [a.canton, a.ville].filter(Boolean).join(' · ') || null,
  ].filter(Boolean)
  const modelHoverDetails = [formatVehicleDisplayTitle(a.titre), ...subtitleParts, a.garantie ? 'Garantie' : null].filter(Boolean).join(' · ')

  const concurrentsNb = concurrenceRow?.concurrence?.nb_concurrents
  const concurrentsNbNum = concurrentsNb != null && Number.isFinite(Number(concurrentsNb)) ? Number(concurrentsNb) : null

  const pillChipStyle = (tone) => {
    if (tone === 'amber')
      return {
        bg: 'rgba(224,184,120,0.12)',
        border: '1px solid rgba(224,184,120,0.35)',
        color: '#fde68a',
      }
    if (tone === 'bad')
      return {
        bg: 'rgba(248,113,113,0.1)',
        border: '1px solid rgba(248,113,113,0.32)',
        color: '#fecaca',
      }
    if (tone === 'good')
      return {
        bg: 'rgba(74,222,128,0.1)',
        border: '1px solid rgba(74,222,128,0.32)',
        color: '#bbf7d0',
      }
    return {
      bg: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(148,163,184,0.22)',
      color: 'var(--text-secondary)',
    }
  }

  const concBtn = concurrenceBadgeCompact(concurrenceRow)
  const concFull = concurrenceRow?.concurrence
  const showSituationLine = !concBtn && concFull?.situation
  const stBadge = showSituationLine ? situationBadgeLarge(concFull.situation) : null

  const alert = getPriorityAlert(a, reco)
  const scale = !a.skip_cotation && a.ecart_pct != null ? marketScaleFromAnnonce(a) : null
  const ec = Number(a.ecart_pct)
  const refLabel = cotationReferenceLabel(a)

  const sims = Array.isArray(a.annonces_similaires) ? a.annonces_similaires.slice(0, 5) : []
  const photoUrls = getStockPhotoUrls(a)
  const nbRef = a.nb_annonces_reference != null ? Number(a.nb_annonces_reference) : 0
  // FIX 2026-05 : marquer les cotations construites sur un échantillon trop
  // mince. Seuil empirique : <5 comparables (annonces + ventes confondues)
  // OU confiance moteur "faible" → badge "Fiabilité limitée" + tooltip.
  // On NE retire PAS la cotation : on signale au concessionnaire que le
  // verdict est à prendre avec des pincettes.
  const nbVentesRef = a.nb_ventes_reference != null ? Number(a.nb_ventes_reference) : 0
  const totalComparables = (Number.isFinite(nbRef) ? nbRef : 0) + (Number.isFinite(nbVentesRef) ? nbVentesRef : 0)
  const confianceFaible = String(a.confiance || '').toLowerCase() === 'faible'
  const isLowConfidence = !a.skip_cotation && Number.isFinite(ec) && (totalComparables < 5 || confianceFaible)
  const lowConfidenceReason = (() => {
    if (!isLowConfidence) return ''
    const parts = []
    if (totalComparables < 5) {
      parts.push(`${totalComparables} comparable${totalComparables > 1 ? 's' : ''} (annonces + ventes)`)
    }
    if (confianceFaible) parts.push('confiance moteur faible')
    return parts.join(' · ')
  })()
  const stockPrix = Number(a.prix)
  const synthText = a.analyse_ia && !shouldHideGenericSynth(a.analyse_ia) ? a.analyse_ia : null
  const synthFallback =
    !synthText && reco && typeof reco.detail === 'string' && !alert
      ? `${reco.action ? `${reco.action} — ` : ''}${reco.detail}`
      : null

  const completeness = getAnnonceCompletenessIssues(a)
  const isIncomplete = completeness.incomplete

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Détail annonce"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6500,
        background: 'rgba(10,12,14,0.55)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'stretch',
        overflowX: 'hidden',
        touchAction: 'pan-y',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxWidth: '100vw',
          height: '100%',
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflowX: 'hidden',
        }}
      >
        {/* Header sticky */}
        <div
          style={{
            flexShrink: 0,
            padding: '16px 18px 12px',
            borderBottom: '1px solid rgba(148,163,184,0.14)',
            background: 'var(--surface)',
            zIndex: 3,
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, width: 80 }}>
              {a.image_url ? (
                <img
                  src={a.image_url}
                  alt=""
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: RADIUS_BLOCK, background: 'var(--surface-alt)' }}
                />
              ) : (
                <div style={{ width: 80, height: 80, borderRadius: RADIUS_BLOCK, background: 'var(--surface-alt)', border: '1px solid var(--border)' }} />
              )}
              {a.url ? (
                <a
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', marginTop: 6, fontSize: 12, fontWeight: 600, color: '#93c5fd', textDecoration: 'none' }}
                >
                  Ouvrir l&apos;annonce
                </a>
              ) : null}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 36, position: 'relative' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, lineHeight: 1.25 }}>
                {a.url ? (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    title={modelHoverDetails}
                    style={{ color: 'var(--text)', textDecoration: 'none', cursor: 'pointer' }}
                  >
                    {formatVehicleDisplayTitle(a.titre)}
                  </a>
                ) : (
                  <span style={{ color: 'var(--text)' }} title={modelHoverDetails}>
                    {formatVehicleDisplayTitle(a.titre)}
                  </span>
                )}
              </h2>
              {subtitleParts.length ? (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{subtitleParts.join(' · ')}</p>
              ) : null}
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {!a.skip_cotation && Number.isFinite(ec) ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: RADIUS_PILL,
                      fontSize: 11,
                      fontWeight: 650,
                      ...pillChipStyle(ec > 8 ? 'bad' : ec < -8 ? 'good' : 'neutral'),
                    }}
                  >
                    {ec > 0 ? '+' : ''}
                    {Number(ec).toFixed(1)} % vs marché
                  </span>
                ) : null}
                {isLowConfidence ? (
                  <span
                    title={`Cotation calculée sur ${totalComparables} comparable${totalComparables > 1 ? 's' : ''} (annonces actives + ventes confirmées). À interpréter avec prudence : un échantillon réduit peut produire une référence biaisée.`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 10px',
                      borderRadius: RADIUS_PILL,
                      fontSize: 11,
                      fontWeight: 650,
                      border: '1px solid rgba(251,191,36,0.45)',
                      background: 'rgba(120,53,15,0.18)',
                      color: '#fde68a',
                      letterSpacing: '0.02em',
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>!</span>
                    Fiabilité limitée
                  </span>
                ) : null}
                {a.garantie ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: RADIUS_PILL,
                      fontSize: 11,
                      fontWeight: 650,
                      ...pillChipStyle('good'),
                    }}
                  >
                    Garantie
                  </span>
                ) : null}
                {!concBtn && concurrentsNbNum != null ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: RADIUS_PILL,
                      fontSize: 11,
                      fontWeight: 650,
                      ...pillChipStyle('neutral'),
                    }}
                  >
                    {concurrentsNbNum} concurrent{concurrentsNbNum !== 1 ? 's' : ''} Pro
                  </span>
                ) : null}
              </div>
              {a.skip_cotation ? (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 8,
                    padding: '3px 8px',
                    borderRadius: RADIUS_PILL,
                    border: '1px solid rgba(96,165,250,0.35)',
                    background: 'rgba(96,165,250,0.08)',
                    color: '#93c5fd',
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                  }}
                >
                  Neuf / quasi-neuf
                </span>
              ) : null}
              <button
                type="button"
                aria-label="Fermer"
                onClick={onClose}
                className="drawer-close-btn"
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.35)',
                  background: 'rgba(15,23,42,0.72)',
                  color: '#dbeafe',
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                ←
              </button>
            </div>
          </div>
        </div>

        {/* Scroll body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '0 18px 12px' }}>
          {isIncomplete ? (
            <div
              style={{
                marginTop: 16,
                padding: '14px 16px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid rgba(220,38,38,0.55)',
                background: 'rgba(220,38,38,0.10)',
                color: '#fecaca',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span
                  aria-hidden
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: 'rgba(220,38,38,0.35)',
                    color: '#fee2e2',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  !
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.01em', color: '#fee2e2' }}>
                  Annonce incomplète
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55, color: '#fecaca' }}>
                {describeIncompleteAnnonce(completeness)}
              </p>
              {a.url ? (
                <a
                  href={a.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 12,
                    padding: '8px 12px',
                    borderRadius: RADIUS_PILL,
                    border: '1px solid rgba(220,38,38,0.55)',
                    background: 'rgba(220,38,38,0.18)',
                    color: '#fee2e2',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    textDecoration: 'none',
                  }}
                >
                  Ouvrir l&apos;annonce sur MotoScout
                </a>
              ) : null}
            </div>
          ) : null}
          {isLowConfidence && !isIncomplete ? (
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid rgba(251,191,36,0.45)',
                background: 'rgba(120,53,15,0.18)',
                color: '#fde68a',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span
                  aria-hidden
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: 'rgba(251,191,36,0.3)',
                    color: '#fef3c7',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  !
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.01em' }}>
                  Cotation potentiellement peu fiable
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: '#fde68a' }}>
                Échantillon réduit&nbsp;:&nbsp;{lowConfidenceReason || 'comparables limités'}. L&apos;écart vs marché reste affiché à titre indicatif, mais la référence peut être biaisée par le faible volume de données.
              </p>
            </div>
          ) : null}
          {concBtn ? (
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onNavigateConcurrence?.(String(a.model || '').trim().toLowerCase())
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: RADIUS_BLOCK,
                  border: concBtn.border,
                  background: concBtn.bg,
                  color: concBtn.color,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  maxWidth: '100%',
                }}
              >
                {concBtn.text}
              </button>
            </div>
          ) : null}

          {showSituationLine && stBadge ? (
            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: RADIUS_PILL,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  background: stBadge.bg,
                  color: stBadge.color,
                  border: stBadge.border,
                }}
              >
                {stBadge.label}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{situationConcurrenceCopy(concFull.situation)}</span>
            </div>
          ) : null}

          {a.skip_cotation ? (
            <div style={{ marginTop: 14, fontSize: 12, color: '#93c5fd', lineHeight: 1.45 }}>
              {a.raison || 'Véhicule quasi-neuf — comparer au prix catalogue plutôt qu’au marché occasion'}
            </div>
          ) : null}

          {alert ? (
            <div
              style={{
                marginTop: 16,
                padding: '12px 14px',
                borderLeft: alert.border,
                borderRadius: '0 6px 6px 0',
                background: alert.bg,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{alert.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{alert.body}</div>
            </div>
          ) : !a.skip_cotation && !reco ? (
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
              Pas d’action automatique prioritaire — surveillez le positionnement prix et la concurrence sur ce modèle.
            </div>
          ) : null}

          {!a.skip_cotation ? (
            <>
              <SectionRule />
              <div
                style={{
                  border: '1px solid rgba(148,163,184,0.14)',
                  borderRadius: RADIUS_BLOCK,
                  background: 'linear-gradient(180deg, rgba(0,30,80,0.14) 0%, rgba(255,255,255,0.02) 100%)',
                  padding: '14px 16px',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    marginBottom: 6,
                  }}
                >
                  Prix annoncé
                </div>
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 300,
                    color: 'var(--text)',
                    letterSpacing: '-0.03em',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatChf(a.prix)}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 14,
                  marginTop: 16,
                }}
              >
                <MetricCell
                  label="Vente probable"
                  value={(() => {
                    const v = Number(a.prix_vente_probable)
                    return Number.isFinite(v) && v > 0 ? formatChf(v) : '—'
                  })()}
                />
                <MetricCell
                  label="Réf. annonces"
                  value={(() => {
                    const v = Number(a.valeur_marche)
                    return Number.isFinite(v) && v > 0 ? formatChf(v) : '—'
                  })()}
                />
                <MetricCell
                  label="Estimation moteur"
                  value={(() => {
                    const v = Number(a.valeur_estimee_moteur)
                    return Number.isFinite(v) && v > 0 ? formatChf(v) : '—'
                  })()}
                />
              </div>
              {(() => {
                const pvp = Number(a?.prix_vente_probable)
                const vm = Number(a?.valeur_marche)
                const vem = Number(a?.valeur_estimee_moteur)
                const hasPvp = Number.isFinite(pvp) && pvp > 0
                const hasVm = Number.isFinite(vm) && vm > 0
                const hasVem = Number.isFinite(vem) && vem > 0
                if (!hasPvp && !hasVm && !hasVem) {
                  return (
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 10 }}>
                      Référence marché : données indisponibles sur cette fiche.
                    </div>
                  )
                }
                return null
              })()}
              {(() => {
                const parts = []
                if (nbRef > 0) parts.push(`${nbRef.toLocaleString('fr-CH')} annonces de référence`)
                if (a.confiance) parts.push(`confiance ${String(a.confiance)}`)
                if (a.dispersion_pct != null && Number.isFinite(Number(a.dispersion_pct))) {
                  parts.push(`dispersion ±${Number(a.dispersion_pct).toFixed(0)} %`)
                }
                const hasMetaCard = Boolean(refLabel) || parts.length > 0
                if (!hasMetaCard) return null
                return (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '11px 12px',
                      borderRadius: RADIUS_BLOCK,
                      border: '1px solid rgba(148,163,184,0.12)',
                      background: 'rgba(255,255,255,0.025)',
                    }}
                  >
                    {refLabel ? (
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', lineHeight: 1.35 }}>{refLabel}</div>
                    ) : null}
                    {parts.length ? (
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: refLabel ? 8 : 0, lineHeight: 1.5 }}>
                        {parts.join(' · ')}
                      </div>
                    ) : null}
                  </div>
                )
              })()}
              {a.prix_reprise != null && Number.isFinite(Number(a.prix_reprise)) && Number(a.prix_reprise) > 0 ? (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 12px',
                    borderRadius: RADIUS_BLOCK,
                    border: '1px solid rgba(74,222,128,0.22)',
                    background: 'rgba(22,101,52,0.08)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#bbf7d0',
                  }}
                >
                  Prix reprise estimé : {Number(a.prix_reprise).toLocaleString('fr-CH')} CHF
                </div>
              ) : null}

              <SectionRule />

              {a.ecart_pct != null ? (
                <>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--text-tertiary)',
                      marginBottom: 8,
                    }}
                  >
                    Fourchette cotation & position prix
                  </div>
                  <MarketPositionScale prix={a.prix} scale={scale} />
                  {a.positionnement && a.positionnement !== 'inconnu' ? (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '11px 12px',
                        borderRadius: RADIUS_BLOCK,
                        border: '1px solid rgba(148,163,184,0.16)',
                        background: 'rgba(255,255,255,0.03)',
                      }}
                    >
                      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 4 }}>
                        Statut moteur
                      </div>
                      <span
                        style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35 }}
                        title={
                          String(a.reference_type || '').toLowerCase() === 'ventes_reelles' &&
                          Number(a.ecart_pct) > 10 &&
                          Number(a.prix_vente_probable) > 0 &&
                          Number(a.valeur_estimee_moteur) > Number(a.prix_vente_probable)
                            ? 'Surcotée vs ventes réelles — le marché affiché peut être plus haut.'
                            : undefined
                        }
                      >
                        {a.positionnement}
                      </span>
                    </div>
                  ) : null}
                </>
              ) : (
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--text-tertiary)',
                      marginBottom: 8,
                    }}
                  >
                    Position prix vs marché
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                    {a.positionnement && a.positionnement !== 'inconnu' ? (
                      <>
                        <strong style={{ color: 'var(--text)', fontSize: 14 }}>{a.positionnement}</strong>
                        {a.fourchette ? (
                          <>
                            <br />
                            <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Fourchette : {a.fourchette}</span>
                          </>
                        ) : null}
                        {a.confiance ? (
                          <>
                            <br />
                            <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Confiance : {a.confiance}</span>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>Écart vs marché non calculé — données de cotation incomplètes ou fourchette indisponible.</>
                    )}
                  </div>
                </div>
              )}
              {token && coterStock ? (
                <MarchePanelInsights
                  token={token}
                  record={{
                    ...a,
                    prix_chf: a.prix_chf ?? a.prix,
                    first_registration_year: a.first_registration_year ?? a.annee,
                  }}
                  variant="marche"
                  showPositionMarcheIntro={false}
                  compactTop
                  omitMarketPositionScale
                  omitHistoriqueChart
                  omitLiquidityBlock
                  omitSimilairesExcerpt
                />
              ) : null}
            </>
          ) : null}

          {a.historique_prix && a.historique_prix.nb_baisses > 0 ? (
            <div
              style={{
                marginTop: 14,
                padding: '11px 12px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid rgba(248,113,113,0.35)',
                background: 'rgba(127,29,29,0.12)',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 650, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fecaca', marginBottom: 4 }}>
                Historique baisses de prix
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fecaca', fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}>
                {a.historique_prix.nb_baisses} mouvement{a.historique_prix.nb_baisses > 1 ? 's' : ''} · −
                {Math.abs(Number(a.historique_prix.baisse_totale) || 0).toLocaleString('fr-CH')} CHF cumulé
                {a.historique_prix.prix_initial != null ? (
                  <span style={{ fontWeight: 500, color: '#fca5a5' }}> · départ {Number(a.historique_prix.prix_initial).toLocaleString('fr-CH')} CHF</span>
                ) : null}
              </div>
            </div>
          ) : null}

          <SectionRule />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <MetricCell label="Vues" value={a.vues != null ? Number(a.vues).toLocaleString('fr-CH') : '—'} />
            <MetricCell label="Leads" value={a.leads != null ? Number(a.leads).toLocaleString('fr-CH') : '—'} />
            <MetricCell label="Concurrents actifs" value={concurrenceRow?.concurrence?.nb_concurrents != null ? String(concurrenceRow.concurrence.nb_concurrents) : '—'} />
          </div>

          {photoUrls.length > 0 ? (
            <>
              <SectionRule />
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 8 }}>
                Photos
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 8 }}>
                {photoUrls.map((u, idx) => (
                  <img key={`${u}-${idx}`} src={u} alt="" style={{ width: '100%', height: 58, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
                ))}
              </div>
            </>
          ) : null}

          <SectionRule />
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-tertiary)',
              marginBottom: 10,
            }}
          >
            Rotation & exposition capital
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '14px 20px',
              marginTop: 0,
            }}
          >
            <MetricCell
              emphasizeValue
              label="En ligne"
              value={a.jours_en_ligne != null ? `${a.jours_en_ligne} j` : '—'}
              sub={joursEnLigneNum != null && joursEnLigneNum > 90 ? 'Rotation à surveiller' : null}
            />
            <MetricCell
              emphasizeValue
              label="Vente estimée (modèle)"
              value={a.jours_estimes_vente != null ? `${a.jours_estimes_vente} j` : '—'}
              sub={a.label_liquidite ? String(a.label_liquidite) : null}
            />
            <MetricCell
              label="Dépréciation"
              value={depData ? `${depData.depreciationParJour} CHF/j` : '—'}
              sub={
                !depData && joursEnLigneNum != null && joursEnLigneNum > 30
                  ? 'Indisponible (référence marché ou historique insuffisant)'
                  : null
              }
            />
            <MetricCell label="Perte cumulée" value={depData ? `−${depData.perteTotale.toLocaleString('fr-CH')} CHF` : '—'} />
            <div style={{ gridColumn: '1 / -1' }}>
              <MetricCell
                label="Liquidité modèle"
                value={
                  a.score_liquidite != null
                    ? `${a.score_liquidite}/10${a.label_liquidite ? ` · ${a.label_liquidite}` : ''}`
                    : '—'
                }
              />
            </div>
          </div>

          {!alert && (synthText || synthFallback) ? (
            <>
              <SectionRule />
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{synthText || synthFallback}</p>
            </>
          ) : a.analyse_ia && shouldHideGenericSynth(a.analyse_ia) ? null : !a.analyse_ia ? (
            <>
              <SectionRule />
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, fontStyle: 'italic' }}>
                Pas de synthèse texte sur cette fiche (moteur ou cache).
              </p>
            </>
          ) : null}

          <SectionRule />

          {sims.length > 0 ? (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                  marginBottom: 8,
                }}
              >
                Annonces similaires marché
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.85fr 0.55fr 0.65fr',
                  gap: 8,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                  padding: '6px 8px',
                  borderBottom: '1px solid rgba(148,163,184,0.18)',
                }}
              >
                <span>Prix</span>
                <span>Km</span>
                <span>Année</span>
                <span style={{ textAlign: 'right' }}>Écart</span>
              </div>
              {sims.map((sim, j) => {
                const p = Number(sim.prix)
                const pctVs =
                  Number.isFinite(stockPrix) && stockPrix > 0 && Number.isFinite(p) ? Math.round(((p - stockPrix) / stockPrix) * 100) : null
                const st = similarRowEcartStyle(pctVs)
                const rowStyle = {
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.85fr 0.55fr 0.65fr',
                  gap: 8,
                  alignItems: 'center',
                  minHeight: 36,
                  padding: '0 8px',
                  fontSize: 12,
                  color: 'var(--text)',
                  textDecoration: 'none',
                  borderBottom: '1px solid rgba(148,163,184,0.1)',
                }
                const inner = (
                  <>
                    <span style={{ fontWeight: 500 }}>{Number(sim.prix || 0).toLocaleString('fr-CH')} CHF</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{sim.km != null ? `${Number(sim.km).toLocaleString('fr-CH')}` : '—'}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{sim.first_registration_year || sim.annee || '—'}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600, ...st }}>
                      {pctVs == null ? '—' : `${pctVs > 0 ? '+' : ''}${pctVs}%`}
                    </span>
                  </>
                )
                return sim.url ? (
                  <a key={j} href={sim.url} target="_blank" rel="noreferrer" style={rowStyle}>
                    {inner}
                  </a>
                ) : (
                  <div key={j} style={rowStyle}>
                    {inner}
                  </div>
                )
              })}
              {nbRef > 0 ? (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>
                  {nbRef > sims.length
                    ? `Voir les ${nbRef.toLocaleString('fr-CH')} annonces de référence (aperçu ${sims.length} lignes).`
                    : `Voir les ${nbRef.toLocaleString('fr-CH')} annonces de référence.`}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Aucune annonce similaire renvoyée pour cette fiche.</div>
          )}

          <div style={{ height: 24 }} />
        </div>

        {/* Sticky footer */}
        <div
          style={{
            flexShrink: 0,
            padding: '12px 18px 16px',
            borderTop: '1px solid rgba(148,163,184,0.18)',
            background: 'rgba(15,17,21,0.96)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (!coterStock?.ok) return
              onCoterMoto?.({
                id_source: a.id_source,
                make: a.make || a.marque || a.brand || 'yamaha',
                model: a.model || a.modele || a.titre || '',
                annee: coterStock.annee,
                km: coterStock.km,
                titre: a.titre,
                version: a.version,
              })
            }}
            disabled={!coterStock?.ok}
            style={{
              width: '100%',
              padding: '11px 14px',
              borderRadius: RADIUS_BLOCK,
              border: `1px solid ${VW_BLUE}`,
              background: VW_BLUE,
              color: '#f8fafc',
              fontSize: 13,
              fontWeight: 600,
              cursor: !coterStock?.ok ? 'not-allowed' : 'pointer',
              opacity: !coterStock?.ok ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            Coter ce véhicule
          </button>
          {!coterStock?.ok ? (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
              Cotation : année ou kilométrage insuffisamment fiables (vérifiez l&apos;annonce ou complétez les champs en base).
            </div>
          ) : null}
          {a.url ? (
            <a
              href={a.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '10px 14px',
                borderRadius: RADIUS_BLOCK,
                border: '1px solid rgba(148,163,184,0.28)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Aller sur l&apos;annonce publique
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}
